import type { KeyboardEvent } from "react";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { usePrevious } from "react-use";

import { getCollectionIcon } from "metabase/common/collections/utils";
import { CollectionDropTarget } from "metabase/common/components/dnd/CollectionDropTarget";
import { TreeNode } from "metabase/common/components/tree/TreeNode";
import type {
  ITreeNodeItem,
  TreeNodeProps,
} from "metabase/common/components/tree/types";
import { focusAdjacentTreeItem } from "metabase/common/components/tree/utils";
import { PLUGIN_COLLECTIONS } from "metabase/plugins";
import { useSelector } from "metabase/redux";
import { getIsTenantUser } from "metabase/selectors/user";
import * as Urls from "metabase/urls";
import type { Collection } from "metabase-types/api";

import {
  CollectionNodeRoot,
  ExpandToggleButton,
  FullWidthContainer,
  FullWidthLink,
  NameContainer,
  SidebarIcon,
} from "./SidebarItems.styled";

type DroppableProps = {
  hovered: boolean;
  highlighted: boolean;
};

type Props = DroppableProps &
  Omit<TreeNodeProps, "item"> & {
    nonNavigable?: boolean;
    collection: Collection;
  };

const TIME_BEFORE_EXPANDING_ON_HOVER = 600;

const SidebarCollectionLink = forwardRef<HTMLLIElement, Props>(
  function SidebarCollectionLink(
    {
      collection,
      nonNavigable,
      hovered: isHovered,
      depth,
      onSelect,
      isExpanded,
      isSelected,
      hasChildren,
      onToggleExpand,
      rightSection,
      isTabbable,
      onFocusItem,
    }: Props,
    ref,
  ) {
    const wasHovered = usePrevious(isHovered);
    const timeoutId = useRef<number>();
    const isTenantUser = useSelector(getIsTenantUser);

    useEffect(() => {
      const justHovered = !wasHovered && isHovered;

      if (justHovered && !isExpanded) {
        timeoutId.current = window.setTimeout(() => {
          if (isHovered) {
            onToggleExpand();
          }
        }, TIME_BEFORE_EXPANDING_ON_HOVER);
      }

      return () => clearTimeout(timeoutId.current);
    }, [wasHovered, isHovered, isExpanded, onToggleExpand]);

    const url = Urls.collection(collection);

    const onKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        switch (event.key) {
          case "ArrowRight":
            if (hasChildren && !isExpanded) {
              onToggleExpand();
            }
            break;
          case "ArrowLeft":
            if (hasChildren && isExpanded) {
              onToggleExpand();
            }
            break;
          case "ArrowDown":
            event.preventDefault();
            focusAdjacentTreeItem(event.currentTarget, 1);
            break;
          case "ArrowUp":
            event.preventDefault();
            focusAdjacentTreeItem(event.currentTarget, -1);
            break;
          case "Enter":
          case " ":
            // This row itself is now the sole Tab stop (see the roving
            // tabIndex below) — the actual link/toggle inside it no longer
            // receives real DOM focus, so it no longer activates on Enter
            // for free the way a natively-focused <a> would. Replicate
            // that here instead of duplicating navigation/toggle logic.
            event.preventDefault();
            if (nonNavigable) {
              onToggleExpand();
            } else {
              event.currentTarget
                .querySelector<HTMLElement>("a[href]")
                ?.click();
            }
            break;
        }
      },
      [isExpanded, hasChildren, onToggleExpand, nonNavigable],
    );

    const icon = getCollectionIcon(collection, { isTenantUser });
    const isRegularCollection =
      PLUGIN_COLLECTIONS.isRegularCollection(collection);

    const content = (
      <>
        <TreeNode.IconContainer transparent={false}>
          <SidebarIcon {...icon} isSelected={isSelected} />
        </TreeNode.IconContainer>
        <NameContainer>{collection.name}</NameContainer>
        {rightSection?.(collection as unknown as ITreeNodeItem)}
      </>
    );

    return (
      <CollectionNodeRoot
        role="treeitem"
        depth={depth}
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
        // Roving tabindex — only the current item is a Tab stop; arrow
        // keys (handled above) move both DOM focus and this marker (WCAG
        // 2.1.1, aria_child_tabbable / widget_tabbable_single). The link/
        // toggle-button inside are always tabIndex={-1}: they stay fully
        // clickable with a mouse, but keyboard activation goes through
        // this row instead, so the tree has exactly one tabbable element
        // at a time, not one per visible link/button.
        tabIndex={isTabbable ? 0 : -1}
        onFocus={onFocusItem}
        onKeyDown={onKeyDown}
        isSelected={isSelected}
        hovered={isHovered}
        onClick={isSelected ? onToggleExpand : undefined}
        hasDefaultIconStyle={isRegularCollection}
        ref={ref}
      >
        <ExpandToggleButton
          hidden={!hasChildren}
          tabIndex={-1}
          onClick={onToggleExpand}
        >
          <TreeNode.ExpandToggleIcon
            isExpanded={isExpanded}
            name="chevronright"
            size={12}
          />
        </ExpandToggleButton>
        {nonNavigable ? (
          <FullWidthContainer tabIndex={-1}>{content}</FullWidthContainer>
        ) : (
          <FullWidthLink to={url} onClick={onSelect} tabIndex={-1}>
            {content}
          </FullWidthLink>
        )}
      </CollectionNodeRoot>
    );
  },
);

const DroppableSidebarCollectionLink = forwardRef<HTMLLIElement, TreeNodeProps>(
  function DroppableSidebarCollectionLink(
    { item, ...props }: TreeNodeProps,
    ref,
  ) {
    const collection = item as unknown as Collection;

    const link = (droppableProps?: DroppableProps) => (
      <SidebarCollectionLink
        {...props}
        hovered={droppableProps?.hovered ?? false}
        highlighted={droppableProps?.highlighted ?? false}
        collection={collection}
        nonNavigable={item.nonNavigable}
        ref={ref}
      />
    );

    return (
      // role="presentation" excludes this wrapper from the accessibility
      // tree so screen readers still see a direct <ul role="tree"> ->
      // [role="treeitem"] relationship (WCAG 1.3.1, list_structure_proper) —
      // it exists only for the test hook and the drop-target ref below.
      <div data-testid="sidebar-collection-link-root" role="presentation">
        {item.nonNavigable ? (
          link()
        ) : (
          <CollectionDropTarget collection={collection}>
            {(droppableProps: DroppableProps) => link(droppableProps)}
          </CollectionDropTarget>
        )}
      </div>
    );
  },
);

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default DroppableSidebarCollectionLink;
